import { dictionaryApi } from './dictionaryClient';
import type { SubantaLinga } from './subantaApi';

/** Vibhakti × three numbers — field names match POST/PATCH JSON. */
export const RUP_VIBHAKTI = [
  'prathama',
  'dwitiya',
  'tritiya',
  'chaturthi',
  'panchami',
  'sasthi',
  'saptami',
  'sambodhan',
] as const;

export const RUP_TRIPLE = ['ekbachan', 'duibachan', 'bahubachan'] as const;

export type RupDeclensionKey = `${(typeof RUP_VIBHAKTI)[number]}_${(typeof RUP_TRIPLE)[number]}`;

export const RUP_DECLENSION_KEYS: RupDeclensionKey[] = (() => {
  const keys: RupDeclensionKey[] = [];
  for (const v of RUP_VIBHAKTI) {
    for (const t of RUP_TRIPLE) {
      keys.push(`${v}_${t}` as RupDeclensionKey);
    }
  }
  return keys;
})();

export type RupScalars = {
  subantaId: number;
  linga: SubantaLinga;
} & Partial<Record<RupDeclensionKey, string | null | undefined>>;

export type Rup = RupScalars & {
  id: number;
  createdAt?: string;
  updatedAt?: string;
  created_at?: string;
  updated_at?: string;
};

export type CreateRupBody = {
  subantaId: number;
  linga: SubantaLinga;
} & Partial<Record<RupDeclensionKey, string | null>>;

export type PatchRupBody = Partial<CreateRupBody>;

export type ListRupsFilters = {
  subantaId?: number;
  linga?: SubantaLinga;
};

export type RupFormValues = {
  linga: SubantaLinga;
} & Record<RupDeclensionKey, string | undefined>;

function normalizeList(data: unknown): Rup[] {
  if (Array.isArray(data)) return data as Rup[];
  if (data && typeof data === 'object' && Array.isArray((data as { data?: unknown }).data)) {
    return (data as { data: Rup[] }).data;
  }
  return [];
}

function normCell(v: string | null | undefined): string | null {
  if (v == null) return null;
  const t = String(v).trim();
  return t === '' ? null : t;
}

export function isRup(o: unknown): o is Rup {
  if (!o || typeof o !== 'object') return false;
  const r = o as Record<string, unknown>;
  return typeof r.id === 'number' && typeof r.subantaId === 'number' && typeof r.linga === 'string';
}

export function rupToFormValues(row: Rup): RupFormValues {
  const cells = {} as Record<RupDeclensionKey, string | undefined>;
  for (const key of RUP_DECLENSION_KEYS) {
    const v = row[key];
    cells[key] = v == null ? undefined : String(v);
  }
  return {
    linga: row.linga,
    ...cells,
  };
}

export function createRupBodyFromForm(
  subantaId: number,
  values: RupFormValues
): CreateRupBody {
  const body: CreateRupBody = {
    subantaId,
    linga: values.linga,
  };
  for (const key of RUP_DECLENSION_KEYS) {
    const cell = values[key]?.trim();
    if (cell) body[key] = cell;
  }
  return body;
}

export function buildRupPatch(prev: Rup, values: RupFormValues): PatchRupBody {
  const patch: PatchRupBody = {};
  if (values.linga !== prev.linga) patch.linga = values.linga;
  for (const key of RUP_DECLENSION_KEYS) {
    const next = normCell(values[key]);
    const prevV = normCell(prev[key] as string | null | undefined);
    if (next !== prevV) {
      patch[key] = next;
    }
  }
  return patch;
}

export async function listRups(filters?: ListRupsFilters): Promise<Rup[]> {
  const res = await dictionaryApi.get<unknown>('/rup', {
    params: {
      ...(filters?.subantaId != null ? { subantaId: filters.subantaId } : {}),
      ...(filters?.linga != null ? { linga: filters.linga } : {}),
    },
  });
  return normalizeList(res.data);
}

export async function getRup(id: number): Promise<Rup> {
  const res = await dictionaryApi.get<Rup>(`/rup/${id}`);
  return res.data;
}

export async function createRup(body: CreateRupBody): Promise<Rup> {
  const res = await dictionaryApi.post<Rup>('/rup', body);
  return res.data;
}

export async function updateRup(id: number, body: PatchRupBody): Promise<Rup> {
  const res = await dictionaryApi.patch<Rup>(`/rup/${id}`, body);
  return res.data;
}

export async function deleteRup(id: number): Promise<void> {
  await dictionaryApi.delete(`/rup/${id}`);
}
